import {FormElement} from "./form-element.js";
import {OptionTools} from "./option-tools.js";
import {OptionElement} from "./option-element.js";

/**
 * The `SelectElement` class represents a form element of type "select".
 * It extends functionality from the `FormElement` class and provides methods
 * to dynamically generate, update, and manipulate a selection dropdown component.
 */
export class SelectElement extends FormElement {
    options;
    select;

    componentTag = "select";
    optionElements;

    /**
     * Constructor for initializing the class with form data, parent element, and collection.
     *
     * @param {Object} formData - The data object containing form-related information.
     * @param {HTMLElement} parentElement - The parent HTML element where the form will be rendered.
     * @param {array} collection - The collection of data items related to the form.
     * @return {void}
     */
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);

        this.optionElements = [];
    }

    /**
     * Updates the attributes of the given component by calling the parent class's attributes method
     * and setting the name property of the component to the value of this instance's dataField.
     *
     * @param {Object} component - The component whose attributes are being updated.
     * @return {void} Does not return a value.
     */
    attributes(component) {
        super.attributes(component);
        component.name = this.dataField;
    }

    /**
     * Initializes the component by calling the parent component's method
     * and setting additional options.
     *
     * @return {Object} The initialized component instance.
     */
    component() {
        this.select = super.component();
        this.setOptions();

        return this.select;
    }

    /**
     * Builds the options list for the Select box
     *
     * @param select The Select to update the options for
     */
    setOptions() {
        this.clearOptions();
        const selectOptions = OptionTools.buildOptions(this, this.options, this.formData);

        let resetValue = true;
        selectOptions.forEach(option => {
            const optionElement = this.setOption(option.text, option.value, option.style);
            if (optionElement.selected === true && (optionElement.value ?? "").length > 0) {
                resetValue = false;
            }

            this.optionElements.push(optionElement);
        });

        if (resetValue) {
            this.setValue(null);
        }

        const values = this.optionElements?.filter(item =>
            item.value !== null && (typeof item.value === "number" || (typeof item.value === "string" && item.value.trim() !== ""))
        );

        if (values.length === 1) {
            if (this.optionElements.length == 2 && (this.optionElements[0]?.value ?? "") === "") {
                this.clearOption(this.optionElements.shift());
            }
            this.setValue(values[0].value);
            values[0].selected = true;
        }

        //if ignoring the placeholder, this sets the selected option to be the first valid value 
        //if the placeholder has been removed, this will be the first value. otherwise, it will be the second in the list.
        if (this.ignorePlaceholder) {
            if (values[0].value) {
                this.setValue(values[0].value);
                values[0].selected = true; 
            } else {
                this.setValue(values[1].value);
                values[1].selected = true;
            }
        }
    }

    /**
     * Sets the visibility of an element by calling the parent class's setVisibility method
     * and passing in a function to handle element display.
     *
     * @return {void} Returns nothing.
     */
    setVisibility() {
        super.setVisibility(() => this.showElement());
    }

    /**
     * Determines whether an element should be shown based on the `postOptionsFilter`
     * and conditions related to filter dependencies and the number of option elements.
     *
     * @return {boolean} Returns `true` if the element should be shown, otherwise `false`.
     */
    showElement() {
        const singleHide = this.postOptionsFilter?.singleHide;
        if (singleHide == null || typeof singleHide !== "object" || Object.keys(singleHide).length === 0) {
            return true;
        }

        const key = OptionTools.postOptionsFilterDepends(this);
        const checkSingle = key != null && (this.postOptionsFilter.singleHide[key] ?? false);

        return !checkSingle || this.optionElements.length > 1 || (this.optionElements?.[0].value ?? "") === "";
    }

    /**
     * Creates the Options tag
     * @param text
     * @param value
     */
    setOption(text, value, style = null) {
        const option = Object.assign(new OptionElement(this.formData, this.select, this.collection), {text, value, style});
        option.render();

        option.selected = this.dataValue === (value ?? text);

        return option;
    }

    /**
     * Removes all the options from the select box
     */
    clearOptions() {
        this.optionElements = this.optionElements ?? [];
        while (this.optionElements.length > 0) {
            this.clearOption(this.optionElements.pop());
        }
    }

    /**
     * Clears the provided option by destroying the specified option element.
     *
     * @param {Object} optionElement - The option element to be cleared and destroyed.
     * @return {void} This method does not return a value.
     */
    clearOption(optionElement) {
        optionElement.destroy();
    }

    /**
     * Updates options dynamically and emits any changes made by a selection
     */
    changeEvent(data) {
        if (!OptionTools.isDynamicOptions(this.options) || this.options.dependsOn !== data.field) {
            return;
        }

        this.setOptions();
    }
}
